import { useGetList, useGetOne } from "react-admin";
import { DrivingLesson } from "../model/autovioCalendarEvents";
import { StartingPoint } from "../providers/startingPointsProvider";
import { Student } from "../providers/studentsProvider";
import { matchAddresses } from "../backoffice.utils";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";

export function useGetDrivingLessonStartingPoint(student?: Student): {
  startingPoint?: StartingPoint;
  isLoading: boolean;
} {
  const drivingSchoolId = student?.drivingSchoolId;
  const { data: drivingLessons = [], isLoading: isDrivingLessonsLoading } = useGetList<DrivingLesson>(
    "studentDrivingLessons",
    {
      filter: {
        studentUid: student?.id,
        drivingSchoolId: student?.drivingSchoolId,
        ...(student?.activeOrMostRecentBookedTraining?.id && {
          bookedTrainingIds: [student.activeOrMostRecentBookedTraining.id],
        }),
      },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "start", order: "DESC" },
    },
    {
      enabled: !!student,
    },
  );
  const drivingLesson = drivingLessons[0];

  const { data: startingPoints = [], isLoading: isStartingPointsLoading } = useGetList<StartingPoint>(
    "startingPoints",
    {
      sort: { field: "__apply_special_starting_point_sort_logic__", order: "ASC" },
      pagination: { page: 1, perPage: 999 },
      meta: { drivingLessonType: drivingLesson?.drivingLessonType, instructorId: drivingLesson?.instructorId, student },
    },
    {
      enabled: !!drivingLesson && drivingLesson.drivingLessonType !== "simulator",
    },
  );

  const { data: drivingSchool, isLoading: isDrivingSchoolLoading } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: drivingSchoolId! },
    { enabled: !!drivingSchoolId },
  );
  const defaultBranch = drivingSchool?.branches.find((branch) => branch.isDefaultBranch);
  // Use the student's assigned branch if available, otherwise fall back to the default branch —
  // matching the mobile app's behavior of always providing a branch address as the default.
  const fallbackBranchId = student?.branchId ?? defaultBranch?.id;

  const { data: fallbackBranchStartingPoint, isLoading: isFallbackBranchStartingPointLoading } =
    useGetOne<StartingPoint>(
      "startingPoints",
      {
        id: `branch ${fallbackBranchId}`,
        meta: {
          student,
          drivingLessonType: drivingLesson?.drivingLessonType ?? "normal",
          instructorId: drivingLesson?.instructorId,
        },
      },
      { enabled: !!fallbackBranchId },
    );

  let startingPoint: StartingPoint | undefined;
  const drivingLessonStartingPoint = startingPoints.find(
    (it) => drivingLesson && matchAddresses(it.postalAddress, drivingLesson.startLocation),
  );

  if (drivingLessonStartingPoint) {
    startingPoint = drivingLessonStartingPoint;
  } else if (fallbackBranchStartingPoint) {
    startingPoint = fallbackBranchStartingPoint;
  }

  return {
    startingPoint,
    isLoading:
      isDrivingLessonsLoading ||
      isStartingPointsLoading ||
      isDrivingSchoolLoading ||
      isFallbackBranchStartingPointLoading,
  };
}
